import { useRef } from 'react';

import { isNull } from 'components/app/_helpers/_app.helper';
import { useSbLogger } from 'components/app/storyboard/_mediator/use-sb-logger';
import { ProgressPayload } from '../_mediator/sb-player.provider';
import { PlayerPayload, PROGRESS_INTERVAL } from '../sb-player-video';
import { useLoaderLimit } from './use-loader-limit';

const TRANSFER_IDLE: Milliseconds = 1000;

type Params = {
  maxLoaderWait: Milliseconds;
  maxLoaderTime: Seconds;
};

/* 
  terminology:
  R -- ready, used for idle players
  B -- buffer, used for active players
*/

export type LoaderPayload = {
  src: 'R' | 'B';
  clipId?: ClipId;
  index: Index;
};

export const useLoader = (
  cb: () => void,
  params: Params,
  loaderPayload: LoaderPayload,
  playerPayload: PlayerPayload
) => {
  const { maxLoaderWait, maxLoaderTime } = params;
  const logger = useSbLogger()?.loader(loaderPayload);

  const progressTimeoutId = useRef<ReturnType<typeof setTimeout>>();
  const pauseTimeoutId = useRef<ReturnType<typeof setTimeout>>();
  const limitTime = useRef<Seconds | null>(null);

  const getPlayerLimitTime = useLoaderLimit(playerPayload, loaderPayload.clipId);

  const onLoaderProgress = (payload: ProgressPayload) => {
    logger?.onProgress(payload.loaded);
    if (isLoaderReady(payload)) endLoader();
    else waitTransferIdle();
  };

  const runLoader = () => {
    waitMaxLoaderTime();
    waitTransferIdle();
  };

  const cancelLoader = () => {
    clearTimeout(pauseTimeoutId.current);
    clearTimeout(progressTimeoutId.current);
    limitTime.current = null;
  };

  const endLoader = () => {
    cancelLoader();
    cb();
  };

  const isLoaderReady = ({ loaded, played, duration }: ProgressPayload) => {
    const leftVideoTime = duration - played;
    if (isNull(limitTime.current)) {
      limitTime.current = getPlayerLimitTime();
      logger?.getLimit(limitTime.current, leftVideoTime);
    }
    const playerLimitTime = limitTime.current;
    const limit1 = playerLimitTime > leftVideoTime ? leftVideoTime : playerLimitTime;
    const limit2 = limit1 < maxLoaderTime ? limit1 : maxLoaderTime;
    return loaded >= limit2;
  };

  /* timers */

  const waitTransferIdle = () => {
    clearTimeout(progressTimeoutId.current);
    progressTimeoutId.current = setTimeout(() => {
      logger?.transferIdle();
      endLoader();
    }, PROGRESS_INTERVAL + TRANSFER_IDLE);
  };

  const waitMaxLoaderTime = () => {
    clearTimeout(pauseTimeoutId.current);
    pauseTimeoutId.current = setTimeout(() => {
      logger?.maxLoaderTime();
      endLoader();
    }, maxLoaderWait);
  };

  return { onLoaderProgress, runLoader, cancelLoader, isLoaderReady };
};
